import {ClientEvent, type MatrixClient, type MatrixEvent} from 'matrix-js-sdk';
import {
    GroupCallController,
    JsSdkSignalling,
    RTC_PROTOCOL_V1,
    RoomCallIndex,
    TokenClient,
    type HttpFetch,
    type RoomCallIndexClient,
    isMediaEncryptionSupported,
} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {browserNotificationService} from '../../../services/browserNotificationService';
import {slidingSyncManager} from '../../../services/slidingSyncManager';
import {getActiveMatrixCallIdentity} from '../../../services/matrixCallService';
import {store} from '../../../store/store';
import {playRingtone, setCallState, stopRingtone} from '../../../store/features/calls/dialerSlice';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {CallStateEnum} from '../../../components/Dialer/callState';
import {isServiceParticipant} from '../../../utils/aiBotUtils';
import {WebRtcPlatform} from '../platform/WebRtcPlatform';
import {createMatrixAccessTokenSource} from '../platform/matrixAccessTokenSource';
import {groupCallRtcLogger} from '../platform/groupCallLogger';
import {ensureRtcPowerLevels, type RtcPowerLevelClientPort} from './rtcPowerLevels';
import {onGroupCallExpandRequested} from './groupCallExpandRequests';
import {mediaUnavailableCopy} from './mediaUnavailable';
import {createRingClient} from './ringClient';
import {createToDeviceRingDelivery, parseTargetedRing} from './targetedRings';
import {teamApi} from '../../../store/features/Team/teamService';
import {
    GroupCallOrchestrator,
    type GroupCallBlocker,
    type GroupCallDialerPort,
    type GroupCallNotifierPort,
    type GroupCallRuntime,
} from './groupCallOrchestrator';

const BLOCKED_COPY: Record<GroupCallBlocker, {title: string; description: string}> = {
    disabled: {title: 'Room calls are unavailable', description: 'Room calls are not enabled for your organisation.'},
    no_session: {title: 'Chat is not connected', description: 'Wait for chat to connect, then try again.'},
    not_owner: {title: 'Calls run in another tab', description: 'Use the browser tab where chat is active to join calls.'},
    busy: {title: 'Another call is active', description: 'End the current call before joining a room call.'},
};

const browserFetch: HttpFetch = (input, init) => window.fetch(input, init);

const CALL_NOTIFICATION_SLACK_MS = 60_000;

const powerLevelPort = (client: MatrixClient): RtcPowerLevelClientPort => ({
    getUserId: () => client.getUserId(),
    getRoom: roomId => client.getRoom(roomId),
    getStateEvent: (roomId, eventType, stateKey) => client.getStateEvent(roomId, eventType, stateKey),
    sendStateEvent: (roomId, eventType, content, stateKey) =>
        client.sendStateEvent(roomId, eventType as never, content as never, stateKey),
});

export const createGroupCallRuntime = (client: MatrixClient): GroupCallRuntime | undefined => {
    const localUserId = client.getUserId();
    if (!localUserId) {
        return undefined;
    }
    const platform = new WebRtcPlatform();
    const tokens = new TokenClient({
        homeserverUrl: client.getHomeserverUrl(),
        accessTokens: createMatrixAccessTokenSource({
            currentToken: () => client.getAccessToken(),
            revalidate: () => matrixService.revalidateAccessToken(),
        }),
        fetch: browserFetch,
        fallbackSfuUrl: window.__ENV__?.MATRIX_RTC_SFU_URL || undefined,
        logger: groupCallRtcLogger,
    });
    const ring = createRingClient();
    const controller = new GroupCallController({
        signallingFactory: (roomId, logger) => new JsSdkSignalling({client, roomId, logger}),
        tokens,
        platform,
        ring,
        ringDelivery: createToDeviceRingDelivery(client),
        isRoomEncrypted: roomId => client.getRoom(roomId)?.hasEncryptionStateEvent() ?? false,
        logger: groupCallRtcLogger,
    });
    const roomCalls = new RoomCallIndex(client as unknown as RoomCallIndexClient);
    return {
        controller,
        roomCalls,
        localUserId,
        rooms: {
            roomName: roomId => client.getRoom(roomId)?.name || roomId,
            memberName: (roomId, userId) => client.getRoom(roomId)?.getMember(userId)?.name || userId,
            isJoinedMember: (roomId, userId) => client.getRoom(roomId)?.getMember(userId)?.membership === 'join',
            joinedMemberCount: roomId => client.getRoom(roomId)?.getJoinedMembers()
                .filter(member => !isServiceParticipant(member.userId)).length ?? 0,
            latestCallNotification: (roomId, since) => client.getRoom(roomId)?.getLiveTimeline().getEvents()
                .filter(event => event.getType() === RTC_PROTOCOL_V1.notificationEventType && (!since || event.getTs() >= since - CALL_NOTIFICATION_SLACK_MS))
                .at(-1)?.getId() ?? undefined,
        },
        ringDirectory: {targets: roomId => ring.targets(roomId), activeInvites: () => ring.activeInvites()},
        ensurePowerLevels: roomId => ensureRtcPowerLevels(powerLevelPort(client), roomId),
        setDevicePreferences: devices => platform.setDevicePreferences(devices),
    };
};

export const dialerBusyPort: GroupCallDialerPort = {
    isOneToOneBusy: () => {
        const dialer = store.getState().dialer;
        const telcoOrMatrixActive = dialer.callState !== CallStateEnum.IDLE && dialer.callState !== CallStateEnum.GROUP_CALL;
        return telcoOrMatrixActive || dialer.incomingCall || !!getActiveMatrixCallIdentity();
    },
    setGroupCallActive: active => {
        const current = store.getState().dialer.callState;
        if (active && current === CallStateEnum.IDLE) {
            store.dispatch(setCallState(CallStateEnum.GROUP_CALL));
        } else if (!active && current === CallStateEnum.GROUP_CALL) {
            store.dispatch(setCallState(CallStateEnum.IDLE));
        }
    },
};

const notifier: GroupCallNotifierPort = {
    showRing: incoming => browserNotificationService.showCallNotification({
        roomId: incoming.roomId,
        title: incoming.callerName,
        body: `${incoming.intent === 'video' ? 'Group video call' : 'Group call'} · ${incoming.roomName}`,
        tag: `group-call-${incoming.notificationEventId}`,
    }),
    showCallStarted: details => {
        store.dispatch(showToast({
            title: `${details.callerName} started a ${details.intent === 'video' ? 'video ' : ''}call`,
            description: details.roomName,
            type: 'info',
            navigateToRoom: details.roomId,
        }));
    },
    showBlocked: blocker => {
        store.dispatch(showToast({...BLOCKED_COPY[blocker], type: 'error'}));
    },
    showMediaUnavailable: kinds => {
        store.dispatch(showToast({...mediaUnavailableCopy(kinds), type: 'warning'}));
    },
};

export const groupCallService = new GroupCallOrchestrator({
    dialer: dialerBusyPort,
    ringer: {
        start: () => {
            store.dispatch(playRingtone());
        },
        stop: () => {
            if (!store.getState().dialer.incomingCall) {
                store.dispatch(stopRingtone());
            }
        },
    },
    notifier,
    sync: {
        pin: roomId => slidingSyncManager.pinCallRoom(roomId),
        unpin: roomId => slidingSyncManager.unpinCallRoom(roomId),
    },
    isTabOwner: () => matrixService.getState().sessionLockState === 'active' && !!matrixService.getClient(),
    isDoNotDisturb: () => {
        const userId = store.getState().auth.userId;
        if (!userId) {
            return false;
        }
        const statuses = teamApi.endpoints.getStatuses.select({users: [Number(userId)]})(store.getState()).data;
        return statuses?.[0]?.doNotDisturbUserStatus?.enabled === true;
    },
    supportsMediaEncryption: isMediaEncryptionSupported,
});

onGroupCallExpandRequested(() => groupCallService.setMinimized(false));

let attachedClient: MatrixClient | null = null;
let started = false;

const onToDevice = (event: MatrixEvent): void => {
    const ring = parseTargetedRing(event);
    if (ring) {
        groupCallService.handleTargetedRing(ring);
    }
};

const syncRuntime = (): void => {
    const client = matrixService.getClient();
    const ready = !!client && matrixService.getState().clientReady;
    if (!ready || !client) {
        if (attachedClient) {
            attachedClient.off(ClientEvent.ToDeviceEvent, onToDevice);
            attachedClient = null;
            groupCallService.attach(undefined);
        }
        return;
    }
    if (client === attachedClient) {
        return;
    }
    attachedClient?.off(ClientEvent.ToDeviceEvent, onToDevice);
    attachedClient = client;
    client.on(ClientEvent.ToDeviceEvent, onToDevice);
    groupCallService.attach(createGroupCallRuntime(client));
};

const onRtcNotification = ({event}: {event: MatrixEvent}): void => {
    const eventId = event.getId();
    const roomId = event.getRoomId();
    const sender = event.getSender();
    if (!eventId || !roomId || !sender) {
        return;
    }
    groupCallService.handleRtcNotification({
        eventId,
        roomId,
        sender,
        originServerTs: event.getTs(),
        content: event.getContent(),
    });
};

export const ensureGroupCallServiceStarted = (): void => {
    if (started) {
        return;
    }
    started = true;
    matrixService.on('stateChange', syncRuntime);
    matrixService.on('rtcNotification', onRtcNotification);
    syncRuntime();
};
